/**
 * Housing Evidence Vault — /housing/evidence-vault
 * Shows evidence categories connected to page modules.
 * Makes the relationship between evidence and assessment visible.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { ContextualGuide } from "@/components/HousingContextualGuide";
import { invalidateHousingCache } from "@/hooks/useHousingJourney";

import { RefreshCw, ArrowLeft, FolderOpen, Link, Upload, CheckCircle2, AlertCircle, BarChart2, Shield, GitBranch, FileText, X, AlertTriangle } from "lucide-react";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const EVIDENCE_CATEGORIES = [
  {
    id: "materials",
    title: "Material Certificates",
    icon: FolderOpen,
    color: "bg-orange-50 border-orange-200",
    iconColor: "text-orange-600",
    linkedTo: ["Climate Snapshot", "MRV Plan", "PDD Section 3"],
    items: [
      { linkedParam: "scm_substitution_pct", label: "SCM cement supplier certificate", status: "required", desc: "Confirms SCM fraction (40%)", trace: "Cement qty → Emission Calc #EMB-CEM-001 → Embodied: 3.85t" },
      { linkedParam: "steel_recycled_pct", label: "EAF steel mill certificate", status: "required", desc: "Confirms recycled content (50%)", trace: "Steel recycled% → Emission Calc #EMB-STL-001 → Embodied: 0.99t" },
      { linkedParam: "cement_kg", label: "Bill of Quantities (BOQ)", status: "available", desc: "Material quantities per unit", trace: "BOQ → All embodied calcs → Baseline 8.28t; Project 6.64t" },
    ],
  },
  {
    id: "design",
    title: "Design Assumptions",
    icon: BarChart2,
    color: "bg-blue-50 border-blue-200",
    iconColor: "text-blue-600",
    linkedTo: ["Input Data", "Baseline Scenario", "PDD Section 2"],
    items: [
      { label: "Architectural drawings / floor plan",    status: "available", desc: "90m2, 3BR confirmed" },
      { label: "Structural specification",               status: "required",  desc: "Confirm steel sections and masonry spec" },
      { label: "Demo unit completion certificate",       status: "available", desc: "InnovateUK project sign-off" },
    ],
  },
  {
    id: "energy",
    title: "Energy Data",
    icon: Shield,
    color: "bg-teal-50 border-teal-200",
    iconColor: "text-teal-600",
    linkedTo: ["Climate Snapshot (Operational)", "MRV Plan", "Carbon Structuring"],
    items: [
      { linkedParam: "annual_energy_kwh", label: "Utility bills (annual electricity)", status: "required", desc: "Validates 4,200 kWh/yr baseline", trace: "kWh/yr → Emission Calc #OP-GRID-001 → Operational: 2.35t/yr" },
      { linkedParam: "solar_fraction_pct", label: "Solar inverter monitoring data", status: "required", desc: "Validates 30% PV offset", trace: "Solar kWh → Emission Calc #OP-SOL-001 → Operational: 1.22t/yr" },
      { linkedParam: "annual_energy_kwh", label: "Energy audit report",                    status: "pending",   desc: "Confirms operational assumptions" },
    ],
  },
  {
    id: "mrv",
    title: "MRV Evidence",
    icon: Shield,
    color: "bg-emerald-50 border-emerald-200",
    iconColor: "text-emerald-600",
    linkedTo: ["MRV Plan", "PDD Section 8", "Carbon Structuring"],
    items: [
      { linkedParam: "cement_kg", label: "Monitoring protocol (signed)", status: "required", desc: "Formal MRV commitment", trace: "Protocol → MRV Plan Section 3 → Parameter Table" },
      { linkedParam: "cement_kg", label: "Baseline survey data", status: "available", desc: "DeCarbonIQ DPCS-H v1.0 output", trace: "Baseline → PDD Section 3 → Emission Reduction = 1.64t" },
      { linkedParam: "cement_kg", label: "Site inspection report",                 status: "pending",   desc: "Third-party site confirmation" },
    ],
  },
  {
    id: "financial",
    title: "Financial Evidence",
    icon: FileText,
    color: "bg-amber-50 border-amber-200",
    iconColor: "text-amber-600",
    linkedTo: ["Finance Brief", "Carbon Structuring", "Financing Page"],
    items: [
      { linkedParam: "capex_usd", label: "Procurement invoices (materials)", status: "required", desc: "Validates incremental $4,500/unit capex", trace: "Invoices → Additionality Section 6 → Financial barrier: payback 24.6yr" },
      { linkedParam: "capex_usd", label: "Conventional construction cost quote", status: "required", desc: "Establishes baseline cost for additionality", trace: "Baseline quote → IRR analysis → Without carbon IRR ~4-6%" },
      { linkedParam: "solar_fraction_pct", label: "Energy savings documentation", status: "pending", desc: "Confirms $182/yr/unit energy savings", trace: "Savings data → Finance Brief Section 3 → Payback calculation" },
    ],
  },
];

const STATUS_CONFIG = {
  available: { label: "Available",  color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  required:  { label: "Required",   color: "bg-amber-100 text-amber-700",    icon: AlertCircle  },
  pending:   { label: "Pending",    color: "bg-slate-100 text-slate-500",    icon: AlertCircle  },
};

// ── Upload Modal ─────────────────────────────────────────────────────────
function UploadModal({ item, onClose, onSuccess }) {
  const inputRef   = useRef(null);
  const xhrRef     = useRef(null);
  const [file, setFile]             = useState(null);
  const [stage, setStage]           = useState("idle");   // idle | hashing | uploading | verifying | complete | error | duplicate
  const [progress, setProgress]     = useState(0);
  const [error, setError]           = useState(null);
  const [isDuplicateHash, setIsDuplicateHash] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const STAGE_LABELS = {
    idle:       { text: "Select a file to upload",       color: "text-slate-500"  },
    hashing:    { text: "Computing SHA-256 hash...",      color: "text-teal-600"   },
    uploading:  { text: "Uploading to secure storage...", color: "text-blue-600"   },
    verifying:  { text: "Verifying hash integrity...",    color: "text-teal-600"   },
    complete:   { text: "Upload complete — hash verified",color: "text-emerald-600" },
    error:      { text: "Upload failed",                  color: "text-red-600"    },
    duplicate:  { text: "Identical file already uploaded",color: "text-amber-600"  },
  };

  const computeHash = async (blob) => {
    const buf     = await blob.arrayBuffer();
    const hashBuf = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2,"0")).join("");
  };

  const handleUpload = async (attemptFile = null, attempt = 0) => {
    const target = attemptFile || file;
    if (!target) return;

    setError(null);
    setProgress(0);
    setIsDuplicateHash(false);

    // Stage 1: hash
    setStage("hashing");
    setProgress(10);
    let hash;
    try {
      hash = await computeHash(target);
    } catch {
      setStage("error");
      setError("Could not compute file hash. Please try again.");
      return;
    }

    // Stage 2: upload with XHR for progress tracking
    setStage("uploading");
    setProgress(25);

    const fd = new FormData();
    fd.append("file", target);
    fd.append("declared_hash", hash);

    const token = localStorage.getItem("decarboniq_token");

    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.open("POST", `${process.env.REACT_APP_BACKEND_URL}/api/housing/evidence/${item.evidence_id}/upload`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 60) + 25; // 25–85%
            setProgress(Math.min(pct, 84));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try {
              const err = JSON.parse(xhr.responseText);
              reject(new Error(err?.detail?.error || err?.detail || `HTTP ${xhr.status}`));
            } catch {
              reject(new Error(`Upload failed (HTTP ${xhr.status})`));
            }
          }
        };
        xhr.onerror = () => reject(new Error("Network error — check connection"));
        xhr.onabort = () => reject(new Error("Upload cancelled"));
        xhr.send(fd);
      }).then(result => {
        // Stage 3: verify
        setStage("verifying");
        setProgress(90);

        setTimeout(() => {
          setProgress(100);
          setStage("complete");
          onSuccess({ ...result, linked_param: item.linkedParam });
        }, 600);
      });
    } catch (e) {
      const msg = e.message || "Upload failed";
      if (msg.includes("already uploaded") || msg.includes("version already")) {
        setStage("duplicate");
        setIsDuplicateHash(true);
      } else if (msg.includes("Network") && attempt < 2) {
        // Retry up to 2 times on network failure
        setRetryCount(attempt + 1);
        setTimeout(() => handleUpload(target, attempt + 1), 2000);
        setStage("uploading");
        setError(`Network issue — retrying (${attempt + 1}/2)...`);
      } else {
        setStage("error");
        setError(msg);
      }
    }
  };

  const handleCancel = () => {
    if (xhrRef.current && stage === "uploading") {
      xhrRef.current.abort();
    }
    onClose();
  };

  const STAGE_STEPS = [
    { key: "hashing",   label: "Hash" },
    { key: "uploading", label: "Upload" },
    { key: "verifying", label: "Verify" },
    { key: "complete",  label: "Done" },
  ];
  const stageOrder = ["hashing", "uploading", "verifying", "complete"];
  const currentStageIdx = stageOrder.indexOf(stage);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="upload-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="text-sm font-semibold text-slate-900">Upload Evidence</p>
            <p className="text-xs text-slate-400">{item.label}</p>
          </div>
          <button onClick={handleCancel} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Step indicator */}
          {stage !== "idle" && (
            <div className="flex items-center justify-between mb-2">
              {STAGE_STEPS.map((s, i) => {
                const done    = currentStageIdx > i;
                const active  = currentStageIdx === i;
                return (
                  <React.Fragment key={s.key}>
                    <div className="flex flex-col items-center gap-0.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                        done   ? "bg-emerald-500 border-emerald-500 text-white" :
                        active ? "bg-teal-500 border-teal-500 text-white" :
                                 "bg-white border-slate-200 text-slate-400"
                      }`}>
                        {done ? "✓" : i + 1}
                      </div>
                      <p className={`text-xs ${active ? "text-teal-600 font-bold" : done ? "text-emerald-600" : "text-slate-400"}`}>{s.label}</p>
                    </div>
                    {i < STAGE_STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-1 ${done ? "bg-emerald-400" : "bg-slate-200"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {/* Drop zone */}
          {stage === "idle" && (
            <div
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${file ? "border-teal-300 bg-teal-50" : "border-slate-200 bg-slate-50 hover:border-teal-300"}`}
              data-testid="upload-drop-zone">
              <input ref={inputRef} type="file" className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.csv,.txt,.json"
                onChange={e => { setFile(e.target.files[0]); setError(null); }} />
              {file ? (
                <>
                  <FileText size={24} className="mx-auto text-teal-500 mb-2" />
                  <p className="text-sm font-semibold text-slate-900">{file.name}</p>
                  <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} KB</p>
                </>
              ) : (
                <>
                  <Upload size={24} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Click to select file</p>
                  <p className="text-xs text-slate-400 mt-1">PDF, images, Office docs, CSV — max 10MB</p>
                </>
              )}
            </div>
          )}

          {/* Progress bar */}
          {["hashing","uploading","verifying","complete"].includes(stage) && (
            <div>
              <div className={`h-2.5 rounded-full overflow-hidden ${stage === "complete" ? "bg-emerald-100" : "bg-slate-100"}`}>
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    stage === "complete" ? "bg-emerald-500" :
                    stage === "verifying" ? "bg-teal-400" : "bg-blue-500"
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <p className={`text-xs font-semibold ${STAGE_LABELS[stage]?.color || "text-slate-500"}`}>
                  {STAGE_LABELS[stage]?.text}
                </p>
                <p className="text-xs text-slate-400">{progress}%</p>
              </div>
            </div>
          )}

          {/* Success */}
          {stage === "complete" && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
              <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-xs font-bold text-emerald-800">Uploaded to S3 · Hash verified</p>
                <p className="text-xs text-emerald-600">SHA-256 matched · Audit log created · Linked to calculation run</p>
              </div>
            </div>
          )}

          {/* Duplicate */}
          {stage === "duplicate" && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
              <AlertCircle size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">This exact file is already uploaded. To update, select a different or modified file.</p>
            </div>
          )}

          {/* Error */}
          {error && stage === "error" && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              <AlertTriangle size={13} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-red-700">{error}</p>
                {retryCount < 2 && (
                  <button onClick={() => handleUpload()} className="text-xs text-red-600 underline mt-1">Retry</button>
                )}
              </div>
            </div>
          )}

          {/* Network retry message */}
          {error && stage === "uploading" && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              <RefreshCw size={12} className="text-amber-500 animate-spin flex-shrink-0" />
              <p className="text-xs text-amber-700">{error}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <button onClick={handleCancel}
              className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-50">
              {stage === "complete" ? "Close" : "Cancel"}
            </button>
            {stage === "idle" && (
              <button onClick={() => handleUpload()}
                disabled={!file}
                className="flex-1 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                data-testid="upload-confirm-btn">
                Upload & Verify
              </button>
            )}
            {stage === "error" && (
              <button onClick={() => { setStage("idle"); setError(null); setProgress(0); }}
                className="flex-1 py-2.5 bg-slate-800 text-white text-xs font-bold rounded-xl hover:bg-slate-700">
                Select Different File
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 text-center">SHA-256 computed client-side · verified server-side · stored on S3</p>
        </div>
      </div>
    </div>
  );
}

export default function HousingEvidenceVault() {
  const navigate  = useNavigate();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState("materials");
  const [uploading, setUploading]     = useState(null);  // {category_id, item}
  const [liveStatus, setLiveStatus]   = useState({});    // {param: {status, version, file_name}}
  const { toast } = require("sonner");

  useEffect(() => {
    client.get("/housing/my-journey").then(r => { setJourney(r.data); setLoading(false); }).catch(() => setLoading(false));
    // Load live evidence status from backend
    client.get("/housing/evidence").then(r => {
      const statusMap = {};
      for (const item of (r.data?.items || [])) {
        statusMap[item.linked_parameter] = {
          status:          item.has_file ? "available" : "required",
          version:         item.version_number,
          file_name:       item.file_name,
          file_hash:       item.file_hash,
          uploaded_at:     item.uploaded_at,
          uploaded_by:     item.uploaded_by,
          reviewed_by:     item.reviewed_by,
          reviewed_at:     item.reviewed_at,
          verification_status: item.verification_status,
          linked_calc_version: item.linked_calc_version,
          evidence_id:     item.evidence_id,
        };
      }
      setLiveStatus(statusMap);
    }).catch(() => {});
  }, []);

  const handleUploadSuccess = (result) => {
    setUploading(null);
    invalidateHousingCache(); // Invalidate shared journey cache after evidence upload
    // Update live status
    setLiveStatus(prev => ({
      ...prev,
      [result.linked_parameter]: {
        status:    "available",
        version:   result.version_number,
        file_name: result.file_name,
        file_hash: result.file_hash,
        uploaded_at: result.uploaded_at,
        evidence_id: result.evidence_id,
      },
    }));
    import("sonner").then(({ toast }) => toast.success(`v${result.version_number} uploaded · Hash verified`));
  };

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><RefreshCw size={18} className="animate-spin text-teal-500" /></div>;

  const org = journey?.organization || {};

  const totalRequired  = EVIDENCE_CATEGORIES.flatMap(c => c.items).filter(i => i.status === "required").length;
  const totalAvailable = EVIDENCE_CATEGORIES.flatMap(c => c.items).filter(i => i.status === "available").length;
  const totalItems     = EVIDENCE_CATEGORIES.flatMap(c => c.items).length;

  return (
    <>
      <div className="p-6 lg:p-8 space-y-6">
        <button onClick={() => navigate("/housing-project")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> Housing Project
        </button>

        <div className="flex items-start justify-between mb-6">
          <div>
            
      <PageHeader
        label="{org.name}"
        title="Evidence Vault"
        backUrl="/housing-project"
        backLabel="Housing Project"
      />
            <p className="text-xs text-slate-400 mt-0.5">
              Evidence supports the assessment, reports, and verification readiness. Each category links to specific modules.
            </p>
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            ["Available", totalAvailable, "text-emerald-700"],
            ["Required",  totalRequired,  "text-amber-700"],
            ["Total",     totalItems,     "text-slate-900"],
          ].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-xl px-4 py-3 text-center">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{l}</p>
              <p className={`text-lg font-bold ${c}`}>{v}</p>
            </div>
          ))}
        </div>

        {/* Evidence categories */}
        <div className="space-y-3">
          {EVIDENCE_CATEGORIES.map(cat => {
            const CatIcon = cat.icon;
            const isOpen = expandedCat === cat.id;
            return (
              <div key={cat.id} className={`bg-white border-2 rounded-xl overflow-hidden ${cat.color}`}
                data-testid={`evidence-category-${cat.id}`}>
                <button
                  onClick={() => setExpandedCat(isOpen ? null : cat.id)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/60">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-white`}>
                    <CatIcon size={16} className={cat.iconColor} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">{cat.title}</p>
                    <div className="flex flex-wrap gap-1.5 mt-0.5">
                      {cat.linkedTo.map(link => (
                        <span key={link} className="text-xs text-slate-500 bg-white border border-slate-200 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <Link size={7} /> {link}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">
                    {cat.items.filter(i => i.status === "available").length}/{cat.items.length} available
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-white/80 px-5 py-3 bg-white/40">
                    {/* Contextual guide per category */}
                    <ContextualGuide
                      guideKey={`evidence_${cat.id}`}
                      title={`What verifiers check — ${cat.title}`}
                      collapsed={true}
                    />
                    <div className="space-y-2 mt-3">
                      {cat.items.map((item, i) => {
                        return (
                          <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-white/60 last:border-0">
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-slate-700">{item.label}</p>
                              <p className="text-xs text-slate-400">{item.desc}</p>
                              {item.trace && (
                                <p className="text-xs text-teal-600 font-mono mt-0.5 bg-teal-50 px-1.5 py-0.5 rounded">
                                  {item.trace}
                                </p>
                              )}
                              {/* Live status from backend */}
                              {liveStatus[item.linkedParam] && (
                                <div className="mt-1 space-y-0.5">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-xs text-slate-500 font-mono">
                                      {liveStatus[item.linkedParam].file_name?.slice(0,25)}
                                    </p>
                                    <span className="text-xs text-slate-400">
                                      v{liveStatus[item.linkedParam].version}
                                    </span>
                                    {liveStatus[item.linkedParam].uploaded_by && (
                                      <span className="text-xs text-slate-400">
                                        by {liveStatus[item.linkedParam].uploaded_by.split("@")[0]}
                                      </span>
                                    )}
                                    {liveStatus[item.linkedParam].uploaded_at && (
                                      <span className="text-xs text-slate-400">
                                        {new Date(liveStatus[item.linkedParam].uploaded_at).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                  {liveStatus[item.linkedParam].linked_calc_version && (
                                    <p className="text-xs text-teal-600 font-mono">
                                      Linked to calculation v{liveStatus[item.linkedParam].linked_calc_version}
                                    </p>
                                  )}
                                  {liveStatus[item.linkedParam].reviewed_by && (
                                    <p className="text-xs text-emerald-600">
                                      Verified by {liveStatus[item.linkedParam].reviewed_by.split("@")[0]}
                                      {liveStatus[item.linkedParam].reviewed_at ? ` · ${new Date(liveStatus[item.linkedParam].reviewed_at).toLocaleDateString()}` : ""}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* Live status badge */}
                              {(() => {
                                const live = liveStatus[item.linkedParam];
                                const displayStatus = live?.status || item.status;
                                const sc = STATUS_CONFIG[displayStatus] || STATUS_CONFIG.pending;
                                return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>;
                              })()}
                              {/* Upload button */}
                              <button
                                onClick={() => setUploading({ catId: cat.id, item: { ...item, evidence_id: liveStatus[item.linkedParam]?.evidence_id } })}
                                className="text-xs font-bold text-teal-600 hover:underline flex items-center gap-0.5 flex-shrink-0"
                                data-testid={`upload-btn-${item.linkedParam}`}>
                                <Upload size={9} /> {liveStatus[item.linkedParam]?.status === "available" ? "Re-upload" : "Upload"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Evidence → Verification Readiness</p>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500 leading-relaxed flex-1">
              Evidence completeness directly impacts MRV confidence. Uploading supplier certificates,
              utility bills, and monitoring data upgrades confidence to High.
            </p>
          </div>
          {/* Live upload status summary */}
          {Object.keys(liveStatus).length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-2">Uploaded Files</p>
              <div className="space-y-1">
                {Object.entries(liveStatus).filter(([,v]) => v.status === "available").map(([param, info]) => (
                  <div key={param} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 font-mono">{param}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">{info.file_name?.slice(0,25)}</span>
                      <span className="font-bold text-emerald-600">v{info.version} ✓</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload modal */}
      {uploading && (
        <UploadModal
          item={uploading.item}
          onClose={() => setUploading(null)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Next step CTA: Evidence Vault → Carbon Strategy */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between z-10">
        <p className="text-xs text-slate-400">
          Complete evidence vault before proceeding to carbon pathway assessment.
        </p>
        <button
          onClick={() => navigate("/housing/carbon-strategy")}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 transition-colors"
          data-testid="proceed-to-carbon-strategy">
          Proceed to Carbon Strategy →
        </button>
      </div>
    </>
  );
}
